import axios from 'axios';

const BASE_URL = '/v1/dashboard';

async function get(url, config) {
    const response = await axios.get(url, config);
    return response.data;
}

// Empty object responses mean "keep what is shown" (undefined); empty lists reset to [].
const orKeep = (data) => data || undefined;
const orEmpty = (data) => data || [];

/**
 * Headline figures shown at the top of the dashboard. Each request may fail on its own;
 * only the ones that succeed are returned, keyed by the dashboard state they fill.
 */
export async function getDashboardOverview() {
    const requests = {
        kpi: get(`${BASE_URL}/kpi-summary`).then(orKeep),
        profitSummary: get(`${BASE_URL}/profit-summary`).then(orKeep),
        latestBankBalance: get('/v1/bank/daily-balance/latest').then((data) => data || null),
        cheques: get('/v1/cheques').then(orEmpty),
        financialPosition: get(`${BASE_URL}/financial-position`).then(orKeep),
        performanceSummary: get(`${BASE_URL}/performance-summary`).then(orKeep),
    };
    const keys = Object.keys(requests);
    const results = await Promise.allSettled(Object.values(requests));
    return Object.fromEntries(
        results
            .map((result, index) => [keys[index], result])
            .filter(([, result]) => result.status === 'fulfilled')
            .map(([key, result]) => [key, result.value]),
    );
}

// Lazily loaded sections. Each loads all of its data or fails as a whole.

export async function getTrendSection() {
    const [salesVsPurchases, profitTrend] = await Promise.all([
        get(`${BASE_URL}/sales-vs-purchases`),
        get(`${BASE_URL}/gross-profit-trend`),
    ]);
    return { salesVsPurchases: orKeep(salesVsPurchases), profitTrend: orKeep(profitTrend) };
}

export async function getFinancialTrendsSection() {
    const [financialTrend, creditPayableTrend, creditReceivableTrend, stockValueTrend] = await Promise.all([
        get(`${BASE_URL}/business-profit-expense-trend`),
        get(`${BASE_URL}/credit-payable-trend`),
        get(`${BASE_URL}/credit-receivable-trend`),
        get(`${BASE_URL}/stock-value-trend`),
    ]);
    return {
        financialTrend: orKeep(financialTrend),
        creditPayableTrend: orKeep(creditPayableTrend),
        creditReceivableTrend: orKeep(creditReceivableTrend),
        stockValueTrend: orKeep(stockValueTrend),
    };
}

export async function getItemAnalyticsSection() {
    const [topItemsRevenue, topItemsProfit, categoryRevenue] = await Promise.all([
        get(`${BASE_URL}/top-items-revenue`),
        get(`${BASE_URL}/top-items-profit`),
        get(`${BASE_URL}/category-revenue`),
    ]);
    return {
        topItemsRevenue: orEmpty(topItemsRevenue),
        topItemsProfit: orEmpty(topItemsProfit),
        categoryRevenue: orEmpty(categoryRevenue),
    };
}

export async function getOperationsSection() {
    const [slowMoving, recentBills] = await Promise.all([
        get(`${BASE_URL}/slow-moving`),
        get(`${BASE_URL}/recent-bills`, { params: { limit: 5 } }),
    ]);
    return { slowMoving: orEmpty(slowMoving), recentBills: orEmpty(recentBills).slice(0, 5) };
}

export async function getInventorySection() {
    const [stockAlerts, topVendors] = await Promise.all([
        get(`${BASE_URL}/stock-alerts`),
        get(`${BASE_URL}/top-vendors`),
    ]);
    return { stockAlerts: orKeep(stockAlerts), topVendors: orEmpty(topVendors) };
}
